/*
 * MCRO001-F0050 / T-MCRO-050 (design: choreo MCRO001-D004 §§4.1.3/7 picker, §4.1.1/§5.3 a11y)
 * Slice 2 of 3 — the resolve picker shared by ConflictPicker and SuggestionPicker.
 *
 * Renders ONE AnnotationNode with resolution choices: ours | theirs | both | hand-edit.
 *   - ours       -> { choice: 'ours' }
 *   - theirs     -> { choice: 'theirs' }
 *   - both       -> { choice: 'both' }  (ours-then-theirs concat, computed by the caller
 *                    of the resolution; the picker records the CHOICE, the merge applies it)
 *   - hand-edit  -> { choice: 'edit', text }
 * On a choice it calls onResolve(node.id, resolution). Slice A leaves parsed nodes with an
 * empty id; this component MINTS a UUIDv7 id when the node lacks one (id generation lives
 * HERE, keeping the lexer pure — Slice A's flagged decision).
 *
 * ACCESSIBILITY (D004 §4.1.1/§5.3): the picker is an `alertdialog` region (it interrupts to
 * ask for a decision), fully keyboard operable (native <button>/<textarea>), every control
 * has a screen-reader label, meaning never rides on colour alone (text labels + borders),
 * and it survives 200% zoom (relative units, wrapping). No banned recovery terms appear in
 * any user-facing string. readOnly / viewer-tier sessions get NO resolve controls at all.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ensureNodeId } from '../../lib/annotation/annotationId';

// Plain-language, artist-facing choice labels. Deliberately free of engineer vocabulary
// (see BANNED_RECOVERY_TERMS) — no "draft", "queue", "rebase", etc.
export const CHOICE_LABELS = Object.freeze({
  ours: 'Keep my version',
  theirs: 'Use their version',
  both: 'Keep both, mine first',
  edit: 'Write it myself',
});

/**
 * The ours-then-theirs concatenation used by the `both` choice. Exported so callers and
 * tests share one definition. Joins with a newline only when both sides are non-empty.
 * @param {string} [ours]
 * @param {string} [theirs]
 * @returns {string}
 */
export function concatBoth(ours, theirs) {
  const a = ours == null ? '' : String(ours);
  const b = theirs == null ? '' : String(theirs);
  if (a === '') return b;
  if (b === '') return a;
  return `${a}\n${b}`;
}

/**
 * @typedef {Object} ResolvePickerProps
 * @property {import('../../lib/annotation/annotationNode').AnnotationNode} node
 * @property {(id: string, resolution: import('../../lib/annotation/annotationNode').AnnotationNode['resolution']) => void} onResolve
 * @property {boolean} [readOnly]
 * @property {() => string} [mintId]  id generator (injected in tests)
 */

/**
 * @param {ResolvePickerProps} props
 */
export default function ResolvePicker({ node, onResolve, readOnly = false, mintId }) {
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState(() => node?.ours ?? node?.body ?? '');
  const editRef = useRef(null);

  // Move focus to the free-text field when hand-edit opens (keyboard operability).
  useEffect(() => {
    if (editing) editRef.current?.focus();
  }, [editing]);

  // Mint the id ONCE per mounted node lacking one, so repeated resolves reuse it.
  const idRef = useRef(null);
  const resolveId = useCallback(() => {
    if (idRef.current == null) idRef.current = ensureNodeId(node, mintId);
    return idRef.current;
  }, [node, mintId]);

  const emit = useCallback(
    (resolution) => {
      if (readOnly) return; // viewer-tier / read-only can NEVER resolve.
      onResolve(resolveId(), resolution);
    },
    [readOnly, onResolve, resolveId],
  );

  const chooseBoth = useCallback(() => {
    emit({ choice: 'both', text: concatBoth(node?.ours, node?.theirs) });
  }, [emit, node]);

  const commitEdit = useCallback(() => {
    emit({ choice: 'edit', text: draftText });
    setEditing(false);
  }, [emit, draftText]);

  const title = node?.title || 'Choose which change to keep';

  const sides = useMemo(
    () => [
      { key: 'ours', label: CHOICE_LABELS.ours, value: node?.ours },
      { key: 'theirs', label: CHOICE_LABELS.theirs, value: node?.theirs },
    ],
    [node],
  );

  return (
    <section
      className="annotation-picker"
      role="alertdialog"
      aria-modal="false"
      aria-label={title}
      data-annotation-kind={node?.kind}
      style={BOX_STYLE}
    >
      <header className="annotation-picker__border-label" style={BORDER_LABEL_STYLE}>
        {title}
      </header>

      <div className="annotation-picker__sides" style={SIDES_STYLE}>
        {sides.map((side) => (
          <figure key={side.key} className={`annotation-picker__side annotation-picker__side--${side.key}`} style={SIDE_STYLE}>
            <figcaption style={SIDE_CAPTION_STYLE}>{side.label}</figcaption>
            <pre style={SIDE_TEXT_STYLE} data-testid={`side-${side.key}`}>{side.value ?? ''}</pre>
          </figure>
        ))}
      </div>

      {readOnly ? null : (
        <div className="annotation-picker__choices" role="group" aria-label="Resolve this change" style={CHOICES_STYLE}>
          <button type="button" onClick={() => emit({ choice: 'ours' })} data-testid="choice-ours">
            {CHOICE_LABELS.ours}
          </button>
          <button type="button" onClick={() => emit({ choice: 'theirs' })} data-testid="choice-theirs">
            {CHOICE_LABELS.theirs}
          </button>
          <button type="button" onClick={chooseBoth} data-testid="choice-both">
            {CHOICE_LABELS.both}
          </button>
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-expanded={editing}
            data-testid="choice-edit"
          >
            {CHOICE_LABELS.edit}
          </button>
        </div>
      )}

      {!readOnly && editing ? (
        <div className="annotation-picker__edit" style={EDIT_STYLE}>
          <label htmlFor={`annotation-edit-${resolveId()}`} style={SIDE_CAPTION_STYLE}>
            Type your version
          </label>
          <textarea
            id={`annotation-edit-${resolveId()}`}
            ref={editRef}
            value={draftText}
            onChange={(event) => setDraftText(event.target.value)}
            rows={3}
            style={TEXTAREA_STYLE}
            data-testid="edit-textarea"
          />
          <button type="button" onClick={commitEdit} data-testid="edit-commit">
            Save my version
          </button>
        </div>
      ) : null}
    </section>
  );
}

// Colour-independent styling: borders + text labels carry all meaning; relative units so
// the panel reflows cleanly at 200% zoom.
const BOX_STYLE = { border: '1px solid currentColor', borderRadius: '4px', padding: '0.5rem', margin: '0.25rem 0' };
const BORDER_LABEL_STYLE = { fontWeight: 600, marginBottom: '0.25rem' };
const SIDES_STYLE = { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' };
const SIDE_STYLE = { flex: '1 1 12rem', border: '1px solid currentColor', borderRadius: '4px', padding: '0.25rem', margin: 0 };
const SIDE_CAPTION_STYLE = { fontWeight: 600 };
const SIDE_TEXT_STYLE = { whiteSpace: 'pre-wrap', margin: '0.25rem 0 0', fontFamily: 'inherit' };
const CHOICES_STYLE = { display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.5rem' };
const EDIT_STYLE = { display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.5rem' };
const TEXTAREA_STYLE = { width: '100%', fontFamily: 'inherit' };
